import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import ReactApexChart from "react-apexcharts";
import ApexChartLoader from "../../../common-components/loaders/ApexChartLoader";

const BMIProfileChart = () => {
  const { bmiChart, bmiChartError, bmiChartLoading } = useSelector(
    (state) => state.prospects
  );

  const [bmiStats, setBMIStats] = useState([]);
  const [chartData, setChartData] = useState({
    categories: [],
    data: [],
    yAxisMin: 0,
    yAxisMax: 40,
  });

  useEffect(() => {
    if (
      !bmiChartError &&
      !bmiChartLoading &&
      bmiChart?.status &&
      bmiChart?.data
    ) {
      setBMIStats(bmiChart.data);
    } else {
      setBMIStats([]);
    }
  }, [bmiChart, bmiChartError, bmiChartLoading]);

  useEffect(() => {
    if (bmiStats && bmiStats.length > 0) {
      const sortedStats = [...bmiStats].sort((a, b) => {
        const dateA = new Date(a?.date?.split("-").reverse().join("-"));
        const dateB = new Date(b?.date?.split("-").reverse().join("-"));
        return dateA - dateB;
      });

      const validStats = sortedStats.filter((stat) => {
        if (!stat || !stat.bmi_result) return false;
        const bmi = parseFloat(stat.bmi_result);
        return !isNaN(bmi) && isFinite(bmi) && bmi > 0;
      });

      if (validStats.length === 0) {
        setChartData({
          categories: [],
          data: [],
          yAxisMin: 0,
          yAxisMax: 40,
        });
        return;
      }

      const categories = validStats.map((stat) => stat?.date || "NA");
      const bmiValues = validStats.map((stat) =>
        parseFloat(parseFloat(stat.bmi_result).toFixed(2))
      );

      const minBMI = Math.min(...bmiValues);
      const maxBMI = Math.max(...bmiValues);
      const padding = (maxBMI - minBMI) * 0.1 || 2;

      setChartData({
        categories,
        data: bmiValues,
        yAxisMin: Math.floor(minBMI - padding),
        yAxisMax: Math.ceil(maxBMI + padding),
      });
    } else {
      setChartData({
        categories: [],
        data: [],
        yAxisMin: 0,
        yAxisMax: 40,
      });
    }
  }, [bmiStats]);

  const bmiChartOptions = {
    chart: {
      type: "line",
      toolbar: { show: false },
      zoom: { enabled: false },
    },
    stroke: {
      curve: "smooth",
      width: 3,
    },
    colors: ["#10b981"],
    xaxis: {
      categories: chartData.categories,
      labels: {
        style: { colors: "#6b7280", fontSize: "12px" },
      },
    },
    yaxis: {
      min: chartData.yAxisMin,
      max: chartData.yAxisMax,
      labels: {
        style: { colors: "#6b7280", fontSize: "12px" },
        formatter: (val) => Math.round(val),
      },
    },
    grid: {
      borderColor: "#e5e7eb",
      strokeDashArray: 4,
    },
    markers: {
      size: 6,
      colors: ["#fff"],
      strokeColors: ["#10b981"],
      strokeWidth: 2,
      hover: { size: 8 },
    },
    legend: {
      show: true,
      position: "bottom",
      horizontalAlign: "center",
      fontSize: "16px",
      fontWeight: 400,
      fontFamily: "inherit",
      offsetY: 10,
      markers: {
        width: 10,
        height: 10,
        strokeWidth: 0,
        strokeColor: "#10b981",
        fillColors: ["#10b981"],
        radius: 12,
        offsetX: -5,
      },
      itemMargin: {
        horizontal: 0,
        vertical: 0,
      },
      labels: {
        colors: ["#10b981"],
        useSeriesColors: false,
      },
    },
    tooltip: {
      enabled: true,
      y: {
        formatter: (val) => `${val}`,
      },
    },
  };

  const bmiChartSeries = [
    {
      name: "BMI",
      data: chartData.data,
    },
  ];

  return (
    <div className="tme-analysis-chart-comparison-section">
      <h3 className="tme-analysis-chart-section-title">
        BMI Profile Comparison
      </h3>

      <div className="tme-analysis-chart-chart-container">
        {bmiChartLoading ? (
          <ApexChartLoader />
        ) : (
          <ReactApexChart
            options={bmiChartOptions}
            series={bmiChartSeries}
            type="line"
            height={300}
          />
        )}
      </div>
    </div>
  );
};

export default BMIProfileChart;
